import { z } from 'zod'

export const LEARNING_PUBLICATIONS_CONTRACT_VERSION = '1.0.0' as const
export const LEARNING_PUBLICATIONS_SCHEMA_VERSION = 'learning-publications-v1' as const

const uuid = z.string().uuid()
const jsonObject = z.record(z.unknown())
const requiredText = z.string().trim().min(1).max(240)
const hash = z.string().regex(/^sha256:[a-f0-9]{64}$/)
const schemaVersion = z.object({ schemaVersion: z.literal(LEARNING_PUBLICATIONS_SCHEMA_VERSION) })

export const learningPublicationRequirementRequestSchema = z.object({
  requirementId: z.string().trim().min(1).max(180),
  catalogNodeId: requiredText.max(160),
  required: z.boolean().default(true),
  requiredOutcome: z.enum(['completed', 'passed']),
  acceptedEvidenceStandards: z.array(z.string().trim().min(1).max(160)).max(50).default([]),
}).strict()

export const learningProgramPublicationCreateRequestSchema = schemaVersion.extend({
  certificatePolicy: jsonObject.default({}),
  requirements: z.array(learningPublicationRequirementRequestSchema).default([]),
}).strict()

export const learningPublicationNodeSchema = z.object({
  publicationNodeId: uuid,
  catalogNodeId: requiredText.max(160),
  nodeType: requiredText.max(80),
  slug: requiredText.max(180),
  title: requiredText.max(240),
  description: z.string().nullable(),
  status: requiredText.max(40),
  version: z.string().nullable(),
  metadataJson: jsonObject,
}).strict()

export const learningPublicationEdgeSchema = z.object({
  publicationEdgeId: uuid,
  catalogEdgeId: requiredText.max(180),
  fromCatalogNodeId: requiredText.max(160),
  toCatalogNodeId: requiredText.max(160),
  relationshipType: z.enum(['contains', 'requires']),
  orderIndex: z.number().int().nullable(),
}).strict()

export const learningPublicationMappingSchema = z.object({
  publicationMappingId: uuid,
  catalogMappingId: requiredText.max(180),
  catalogNodeId: requiredText.max(160),
  externalEntityId: requiredText.max(180),
  mappingKind: requiredText.max(80),
  confidence: requiredText.max(80),
  mappingStatus: requiredText.max(40),
  ownerRole: requiredText.max(120),
  notes: z.string().nullable(),
  createdBy: requiredText.max(160),
  updatedBy: requiredText.max(160),
}).strict()

export const learningPublicationRequirementSchema = learningPublicationRequirementRequestSchema.extend({
  publicationId: uuid,
}).strict()

export const learningProgramPublicationSchema = z.object({
  publicationId: uuid,
  programNodeId: requiredText.max(160),
  publicationN: z.number().int().positive(),
  graphHash: hash,
  certificatePolicy: jsonObject,
  frozenAt: z.string().datetime({ offset: true }),
  frozenBy: requiredText.max(160),
  nodes: z.array(learningPublicationNodeSchema),
  edges: z.array(learningPublicationEdgeSchema),
  mappings: z.array(learningPublicationMappingSchema),
  requirements: z.array(learningPublicationRequirementSchema),
}).strict()

export const learningProgramPublicationCreateResponseSchema = z.object({
  contract_version: z.literal(LEARNING_PUBLICATIONS_CONTRACT_VERSION),
  item: learningProgramPublicationSchema,
}).strict()

export const learningProgramPublicationDetailResponseSchema = learningProgramPublicationCreateResponseSchema

export type LearningPublicationRequirementRequest = z.infer<typeof learningPublicationRequirementRequestSchema>
export type LearningProgramPublicationCreateRequest = z.infer<typeof learningProgramPublicationCreateRequestSchema>
export type LearningPublicationNode = z.infer<typeof learningPublicationNodeSchema>
export type LearningPublicationEdge = z.infer<typeof learningPublicationEdgeSchema>
export type LearningPublicationMapping = z.infer<typeof learningPublicationMappingSchema>
export type LearningPublicationRequirement = z.infer<typeof learningPublicationRequirementSchema>
export type LearningProgramPublication = z.infer<typeof learningProgramPublicationSchema>
export type LearningProgramPublicationCreateResponse = z.infer<typeof learningProgramPublicationCreateResponseSchema>
export type LearningProgramPublicationDetailResponse = z.infer<typeof learningProgramPublicationDetailResponseSchema>
